import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection } from 'lexical';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useState } from 'react';

import Button from 'shopper/components/Button';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';

import { INSERT_EMOJI_COMMAND } from '../Plugins/EmojiPlugin';
import Divider from './Divider';

import { TOOLS } from 'constants/textEditor';

const EmojiPickerTool = dynamic(() => import('./EmojiPicker'), { ssr: false });
const ImageTool = dynamic(() => import('./Image'), { ssr: false });
const MentionTool = dynamic(() => import('./Mention'), { ssr: false });
const SendButton = dynamic(() => import('../SendButton'), { ssr: false });
const VideoTool = dynamic(() => import('./Video'), { ssr: false });

const DEFAULT_SELECTED_TOOLS = [
  TOOLS.EMBEDDED_VIDEO,
  TOOLS.EMBEDDED_IMAGE,
  TOOLS.EMOJI,
  TOOLS.MENTION_USER,
];

const Toolbar = ({
  gaEventCategory,
  isSendEnabled = false,
  selectedTools,
  withCancel,
  withSend,
  onCancel,
  onSend,
}) => {
  const selectedItems = selectedTools ?? DEFAULT_SELECTED_TOOLS;

  const [editor] = useLexicalComposerContext();
  const [activeItems, setActiveItems] = useState(() =>
    selectedItems.reduce((prev, curr) => {
      prev[curr] = false;

      return prev;
    }, {})
  );

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    setActiveItems(() =>
      selectedItems.reduce((prev, tool) => {
        prev[tool] = $isRangeSelection(selection) && selection.hasFormat(tool);
        return prev;
      }, {})
    );
  }, [editor, selectedItems]);

  useEffect(() => {
    const cleanup = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });

    return () => {
      cleanup();
    };
  }, [editor]);

  const toggleActiveItem = useCallback((tool) => {
    setActiveItems((prevState) => {
      const result = { ...prevState };

      if (TOOLS.EMOJI === tool) {
        result[tool] = !result[tool];
      }

      return result;
    });
  }, []);

  const onAddEmoji = useCallback(
    (data) => {
      const { unicode } = data.emoji;

      editor.dispatchCommand(INSERT_EMOJI_COMMAND, unicode);
    },
    [editor]
  );

  const isToolSelected = (tool) => selectedItems.includes(tool);

  return (
    <div className="border-b-solid w-full rounded-b-3 border border-t-0 border-neutral-high-500 bg-neutral-high-100 px-4 py-2 peer-focus-within:border-primary-300 dark:border-neutral-high-100 dark:bg-neutral-low-500">
      <Divider />
      <div className="flex w-full items-center gap-x-1">
        {isToolSelected(TOOLS.EMOJI) && (
          <EmojiPickerTool
            active={activeItems.emoji}
            onAddEmoji={onAddEmoji}
            onClick={() => {
              toggleActiveItem(TOOLS.EMOJI);
            }}
          />
        )}
        {isToolSelected(TOOLS.MENTION_USER) && <MentionTool />}
        {isToolSelected(TOOLS.EMBEDDED_IMAGE) && <ImageTool />}
        {isToolSelected(TOOLS.EMBEDDED_VIDEO) && <VideoTool />}
        {withCancel && (
          <Button
            className="ml-auto mr-4"
            size="size4"
            type="neutral-stroke"
            onClick={() => {
              sendEvent({
                category: gaEventCategory,
                action: 'cancel_comment',
              });
              onCancel();
            }}
          >
            {placeholder('actions.cancel')}
          </Button>
        )}
        {withSend && (
          <SendButton
            className={!withCancel && 'ml-auto'}
            disabled={!isSendEnabled}
            gaEventCategory={gaEventCategory}
            onClick={onSend}
          />
        )}
      </div>
    </div>
  );
};

Toolbar.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  isSendEnabled: PropTypes.bool,
  selectedTools: PropTypes.arrayOf(PropTypes.string),
  withCancel: PropTypes.bool.isRequired,
  withSend: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  onSend: PropTypes.func,
};

export default memo(Toolbar);
